import { useMutation, useApolloClient } from '@apollo/react-hooks';
import { ExecutionResult } from 'graphql';
import { MutationResult } from '@apollo/react-common';
import {
  CreateTimelineEntryMutation,
  CreateTimelineEntryMutationVariables,
  ListTimelineEntriesQuery,
  CreateTimelineEntryInput,
  ImageMediaInput,
  VideoMediaInput,
  MeasurementSystem,
} from '../../../types/__generated__';
import { useNotNullableContext } from '../useNotNullableContext';
import { PageMetaContext } from '../../context/PageMetaContext';
import {
  readTimelineEntries,
  writeTimelineEntries,
} from './useListTimelineEntries';
import { TimelineImage } from '../../components/Timeline/timelineTypes';
import { updateActivityTotals } from './updateActivityTotals';
import mutation from './queries/createTimelineEntry.gql';

type CreateTimelineEntry = (
  entry: Pick<
    CreateTimelineEntryInput,
    'type' | 'pageId' | 'pageSlug' | 'message' | 'createdDate' | 'fitness'
  > & {
    media: TimelineImage[];
  },
) => Promise<ExecutionResult<CreateTimelineEntryMutation>>;

export default function useCreateTimelineEntry(
  measurementSystem?: MeasurementSystem,
): [CreateTimelineEntry, MutationResult<CreateTimelineEntryMutation>] {
  const client = useApolloClient();
  const { pageType, pageShortName, isPreview } = useNotNullableContext(
    PageMetaContext,
  );

  const [runMutation, mutationResult] = useMutation<
    CreateTimelineEntryMutation,
    CreateTimelineEntryMutationVariables & {
      measurementSystem?: MeasurementSystem;
    }
  >(mutation, {
    update(cache, { data }) {
      if (!data || !data.createTimelineEntry) {
        return;
      }

      const newTimelineEntry = {
        ...data.createTimelineEntry,
        refreshRequestedTime: null,
      };

      const result = readTimelineEntries(
        pageType,
        pageShortName,
        cache,
        measurementSystem,
      );

      const {
        previousPage,
        previousOwner,
        previousTimeline,
        previousPageInfo,
        previousTimelineNodes,
      } = result;

      const newTimelineData: ListTimelineEntriesQuery = {
        page: {
          ...previousPage,
          owner: previousOwner,
          timeline: {
            ...previousTimeline,
            pageInfo: previousPageInfo,
            nodes: [newTimelineEntry, ...previousTimelineNodes].sort((a, b) => {
              const createdDateValue = (m: typeof a & typeof b): Date =>
                typeof m.createdAt === 'string'
                  ? new Date(m.createdAt)
                  : m.createdAt;
              return (
                createdDateValue(b).valueOf() - createdDateValue(a).valueOf()
              );
            }),
            totalCount: previousTimeline.totalCount + 1,
          },
        },
      };

      writeTimelineEntries(pageType, pageShortName, newTimelineData, cache);
      updateActivityTotals(client, pageType, pageShortName, isPreview);
    },
    context: {
      credentials: 'include',
    },
  });

  const createTimelineEntry: CreateTimelineEntry = entry => {
    return runMutation({
      variables: {
        input: {
          ...entry,
          media: entry?.media?.reduce<{
            imageMedia: ImageMediaInput[];
            videoMedia: VideoMediaInput[];
          }>(
            (acc, media) => {
              if (media.__typename === 'ImageMedia') {
                acc.imageMedia = [{ url: media.url }, ...acc.imageMedia];
              }
              return acc;
            },
            { imageMedia: [], videoMedia: [] },
          ),
        },
        measurementSystem: measurementSystem,
      },
    });
  };

  return [createTimelineEntry, mutationResult];
}
